import {CommonModule, NgIf} from '@angular/common';
import {Component, inject, input, OnDestroy, OnInit} from '@angular/core';
import {FlexModule} from '@angular/flex-layout';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {FloatLabelType, MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {AuthenticationService} from '@modules/authentication';
import {AuthenticationConfigurationService} from '@modules/authentication/core/config/authentication-configuration.service';
import {RegisterLinkComponent} from '@modules/authentication/core/login/register-link/register-link.component';
import {CommunicationCenterService} from '@modules/communication-center';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';
import {DataEntity} from 'octopus-connect';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SharedTranslateModule} from 'shared/shared-translate.module';
import {currentTimestamp} from 'shared/utils/datetime';
import {
    brand,
    brandLogoSvg,
} from '../../../../../settings';
import {HOME_APP_URL} from '../../../../../../../projects/i-love-english/src/app/navigation/navigation.service';

export interface LoginFormErrors {
    login: ValidationErrors;
    password: ValidationErrors;
    newPassword: ValidationErrors;
    confirmPassword: ValidationErrors;
    emailValidation: ValidationErrors;
    licenceValidation: ValidationErrors;
}

@Component({
    selector: 'app-login-form',
    standalone: true,
    imports: [
        NgIf,
        FlexModule,
        FormsModule,
        CommonModule,
        RouterModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        TranslateModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        SharedTranslateModule,
        RegisterLinkComponent,
    ],
    templateUrl: './login-form.component.html',
    styleUrl: './login-form.component.scss'
})
export class LoginFormComponent implements OnInit, OnDestroy {
    public login = input<string>();

    private router = inject(Router);
    private dialog = inject(MatDialog);
    private route = inject(ActivatedRoute);
    private translate = inject(TranslateService);
    private formBuilder = inject(UntypedFormBuilder);
    private authenticationService = inject(AuthenticationService);
    private communicationCenter = inject(CommunicationCenterService);
    private configuration = inject(AuthenticationConfigurationService);

    public loginFormErrors: LoginFormErrors;
    public loginForm: UntypedFormGroup;
    public hide = true;
    public hideNewPassword = true;
    public hideConfirmPassword = true;
    public brand = brand;
    public brandLogoSvg = brandLogoSvg;
    public renewPassword = false;
    public modeSSO = false;
    public newLinkSendSuccess = false;
    public newLinkSendError = false;
    public floatLabelControl: FloatLabelType;
    public isLoading = false;
    public showMessageForReloadPage = false;
    public entButtonAllowed = false; // only allowed for fr language


    public settings = this.configuration.settings;
    private unsubscribeInTakeUntil = new Subject<void>();
    private tralacodeIdText = ''; // by default add nothing
    private oldPassword = '';

    /***** GETTERS *****/

    public get licenceLink(): string {
        return this.settings?.licenceLink;
    }

    private get entButtonAllowedWithoutRules(): boolean {
        return this.settings?.entButtonAllowedWithoutRules;
    }

    private get redirectLearnerToAppAfterLogin(): boolean {
        return this.settings?.redirectLearnerToAppAfterLogin;
    }

    /***** LIFECYCLE HOOKS *****/

    ngOnInit(): void {
        this.loginFormErrors = {
            login: {},
            password: {},
            newPassword: {},
            confirmPassword: {},
            emailValidation: {},
            licenceValidation: {},
        };

        this.floatLabelControl = this.settings.floatLabelControl;

        //ctz-setting
        if (this.entButtonAllowedWithoutRules) {
            this.entButtonAllowed = true;
        } else {
            //citizen-code case
            this.entButtonAllowed = localStorage.getItem('lang') === 'fr' || localStorage.getItem('lang') === null;
            this.communicationCenter.getRoom('current-language').getSubject('id').subscribe(lng => {
                if (lng === 'fr') {
                    this.entButtonAllowed = true;
                } else {
                    this.entButtonAllowed = false;
                }
            });
        }
        if (this.route.snapshot.queryParams.code) {
            this.modeSSO = true;
            this.authenticationService.loginSSO(this.route.snapshot.queryParams.code);
        }

        const loginValidators = this.authenticationService.onlyLoginWithMail()
            ? [Validators.required, Validators.email]
            : [Validators.required];

        this.loginForm = this.formBuilder.group(
            {
                login: ['', loginValidators],
                password: ['', Validators.required]
            }
        );

        this.loginForm.valueChanges.subscribe(formValues => {
            this.onLoginFormValuesChanged();
        });

        if (this.login()) {
            this.loginForm.get('login').setValue(this.login());
        }

        this.authenticationService.errorHttpAuthentication
            .pipe(takeUntil(this.unsubscribeInTakeUntil))
            .subscribe((errorHttp) => {
                this.isLoading = false;
                setTimeout(() => {
                    if (errorHttp.code === 401) {
                        const controlpassword = this.loginForm.get('password');

                        // On ne met pas l'identifiant en erreur, uniquement le mot de passe
                        // Il ne faut pas que l'utilisateur puisse savoir si l'identifiant est bon ou pas (sécurité)
                        controlpassword.setErrors({invalid: true});

                        this.loginFormErrors['password'] = controlpassword.errors;
                    } else if (errorHttp.code === 403) {
                        // email was not validated by email link
                        const controlLogin = this.loginForm.get('login');
                        controlLogin.setErrors({invalid: true});
                        if (errorHttp.data.response.title === 'No valid Licence found.' || errorHttp.data.response.title === 'Expired licence.' || errorHttp.data.response.title === 'Licence start on ') {
                            this.loginFormErrors['licenceValidation'] = controlLogin.errors;
                        } else {
                            this.loginFormErrors['emailValidation'] = controlLogin.errors;
                        }
                    } else {
                        this.showMessageForReloadPage = true;
                    }
                }, 500);
            });
    }

    ngOnDestroy(): void {
        this.isLoading = false;
        this.unsubscribeInTakeUntil.next();
        this.unsubscribeInTakeUntil.complete();
    }

    /***** LOGIC *****/

    /**
     * login user : with optionnal change password possibility force by rules
     */
    private doLogin(): void {
        this.isLoading = true;
        this.resetEmailSendMessages();

        const label = this.tralacodeIdText + this.loginForm.value['login'];
        this.tralacodeIdText = ''; // we renew password after trying to log with account who user tralacodeid
        const pass = this.loginForm.value['password'] !== undefined ? this.loginForm.value['password'] : this.oldPassword; //password store if renew password

        if (label && pass) {
            this.authenticationService
                .authenticateIn(
                    'http',
                    label,
                    pass
                )
                .pipe(takeUntil(this.unsubscribeInTakeUntil))
                .subscribe((user) => {
                    if (
                        this.settings.validateEmailStrategyActivated &&
                        !user.get('email_status')
                    ) {
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    // set value is need for pass form on valid state
                    this.loginForm.controls.login.setValue(label);

                    if (this.isNewPasswordToSet()) {
                        this.addPasswordResetAndRenewControl();
                        this.updatePassword();
                        return;
                    }
                    // must be after condition this.isNewPasswordToSet()
                    if (this.isExpirePassword(user)) {
                        this.oldPassword = pass; // store old password for update password
                        this.addPasswordResetAndRenewControl();
                        this.loginForm.removeControl('password');
                        if (this.loginForm.get('login').value.includes('tralacodeid')) {
                            this.tralacodeIdText = 'tralacodeid'; // we add it later again to login
                        }
                        this.loginForm.patchValue({login: (this.loginForm.get('login').value).replace('tralacodeid', '')});
                        this.renewPassword = true;
                        this.authenticationService.logoutFrom('http');
                        this.isLoading = false;
                        return;
                    }

                    const isTrainer = !!(user.get('role').findIndex((role) => role === 5) >= 0); // tester autrement qu'avec un numero (voir authService)
                    // it's fisrts user trainer connexion and help is active
                    if (
                        user.get('access') &&
                        user.get('first_access') === true &&
                        this.settings.askForHelp &&
                        isTrainer
                    ) {
                        this.help();
                    } else {
                        if (this.authenticationService.isLearner() && this.redirectLearnerToAppAfterLogin) {
                            localStorage.setItem('origin', this.router.url);
                            window.open(HOME_APP_URL(), '_self');
                        } else {
                            this.authenticationService.navigateAfterLogin();
                        }
                    }
                }, error => {
                    console.error(error);
                    this.isLoading = false;
                });
        }
    }

    private addPasswordResetAndRenewControl(): void {
        this.loginForm.addControl('newPassword', this.formBuilder.control('', Validators.required));
        this.loginForm.addControl('confirmPassword', this.formBuilder.control('', Validators.required));
        this.loginForm.setValidators(this.mustMatchAndBeNewPassword('password', 'newPassword', 'confirmPassword'));
    }

    /**
     * custom validator to check that two fields match and are différent of original password
     */
    private mustMatchAndBeNewPassword(
        passwordControlName: string,
        newPasswordcontrolName: string,
        confirmPasswordControlName: string
    ) {
        return (formGroup: UntypedFormGroup) => {
            const controlOriginalPasword = formGroup.controls[passwordControlName];
            const controlNewPassword = formGroup.controls[newPasswordcontrolName];
            const controlConfirmPassword =
                formGroup.controls[confirmPasswordControlName];

            if (
                controlConfirmPassword.errors &&
                !controlConfirmPassword.errors.notMatch
            ) {
                return null;
            }

            if (controlOriginalPasword && controlOriginalPasword?.value && controlOriginalPasword.value === controlNewPassword.value) {
                controlNewPassword.setErrors({samePasswordAsOrigine: true});
            } else {
                controlNewPassword.setErrors(null);
            }

            if (controlNewPassword.value !== controlConfirmPassword.value) {
                controlConfirmPassword.setErrors({notMatch: true});
            } else {
                controlConfirmPassword.setErrors(null);
            }

            return null;
        };
    }

    /**
     * reset messages success and error
     * @private
     */
    private resetEmailSendMessages(): void {
        this.newLinkSendSuccess = false;
        this.newLinkSendError = false;
    }

    /**
     * open modal asking if user need help at first connexion
     */
    private help(): void {
        const data = {
            titleDialog: 'generic.ask.help',
            bodyDialog: 'generic.ask.help.content',
            labelTrueDialog: 'generic.yes',
            labelFalseDialog: 'generic.no',
        };

        this.translate
            .get(data.titleDialog)
            .subscribe((translation: string) => (data.titleDialog = translation));
        this.translate
            .get(data.bodyDialog)
            .subscribe((translation: string) => (data.bodyDialog = translation));
        this.translate
            .get(data.labelTrueDialog)
            .subscribe((translation: string) => (data.labelTrueDialog = translation));
        this.translate
            .get(data.labelFalseDialog)
            .subscribe(
                (translation: string) => (data.labelFalseDialog = translation)
            );

        const confirmDialogRef = this.dialog.open(FuseConfirmDialogComponent, {
            data,
        });

        confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // help page
                this.router.navigate(['home']);
            } else {
                // normal navigate
                this.authenticationService.navigateAfterLogin();
            }
        });
    }

    /**
     * update password and go to program if no error
     */
    private updatePassword(): void {
        this.authenticationService.loggedUser.set(
            'password',
            this.loginForm.value['newPassword']
        );
        this.authenticationService.loggedUser.save().subscribe(
            (_userUpdate: DataEntity) => {
                this.renewPassword = false;
                this.authenticationService.navigateAfterLogin();
            },
            (error) => {
                console.log(error);
                this.isLoading = false;
            }
        );
    }

    /**
     * is user expiration password date is passed
     */
    private isExpirePassword(user: DataEntity): boolean {
        return (
            this.settings.activeChangePasswordStrategy &&
            user.get('expirePassword') &&
            currentTimestamp() >= user.get('expirePassword') &&
            user.get('expirePassword') !== null &&
            user.get('expirePassword') !== undefined
        );
    }

    /**
     * is the new password need to be set
     */
    private isNewPasswordToSet(): boolean {
        return (
            this.settings.activeChangePasswordStrategy &&
            this.loginForm.value['newPassword'] &&
            this.loginForm.value['newPassword'] !== null &&
            this.loginForm.value['newPassword'] !== undefined &&
            this.loginForm.value['newPassword'] !== ''
        );
    }

    /***** EVENTS *****/

    public onLoginClick(): void {
        this.doLogin();
    }

    private onLoginFormValuesChanged(): void {
        this.resetEmailSendMessages();

        for (const field in this.loginFormErrors) {
            if (!this.loginFormErrors.hasOwnProperty(field)) {
                continue;
            }

            // Clear previous errors
            this.loginFormErrors[field] = {};

            // Get the control
            const control = this.loginForm.get(field);
            if (control && control.dirty && !control.valid) {
                this.loginFormErrors[field] = control.errors;
            }
        }
    }

    /**
     * user haven't valdiate is email send a new link to register email
     */
    public onSendLinkToValidateEmailClick(): void {
        this.authenticationService
            .sendNewLinkEmailValidation(this.loginForm.value['login'])
            .subscribe({
                next: (_email) => {
                    this.newLinkSendSuccess = true;
                    const controlLogin = this.loginForm.get('login');
                    controlLogin.setErrors({});
                    this.loginFormErrors['emailValidation'] = controlLogin.errors;
                    this.loginFormErrors['licenceValidation'] = controlLogin.errors;
                    setTimeout(() => {
                        this.newLinkSendSuccess = false;
                    }, 3000);
                },
                error: (error) => {
                    this.newLinkSendError = true;
                    console.error(error);
                }
            });
    }
}
